// RemoteDispatchView — the phone screen for remote dispatch.
//
// No active dispatch  -> compose a new one (+ recent history).
// Active dispatch      -> live event stream + inline approval + cancel.
//
// The actual run happens on the Mac (useRemoteRunner, app open); this view is
// just the client side: create, watch (SSE), approve, cancel.

import { useCallback, useEffect, useState } from 'react';
import { useDispatchStream } from '../../hooks/useDispatchStream.js';
import { sendApproval, requestCancel, listDispatches, getCapabilities, getPresence } from '../../lib/remoteDispatchClient.js';
import DispatchComposer from './DispatchComposer.jsx';
import ApprovalPrompt from './ApprovalPrompt.jsx';
import BrainStatus from './BrainStatus.jsx';
import MacOnlineBadge from './MacOnlineBadge.jsx';
import DispatchHistory from './DispatchHistory.jsx';
import DiffView from './DiffView.jsx';
import FileTreeView from './FileTreeView.jsx';
import TouchButton from '../ui/TouchButton.jsx';

function summarize(ev) {
  const p = ev.payload || {};
  switch (ev.type) {
    case 'assistant':
      return p.content || '';
    case 'tool_call':
      return `chamou ${p.name || 'tool'}`;
    case 'tool_result':
      return `${p.approved === false ? 'negado' : 'resultado'}: ${p.name || 'tool'}`;
    case 'approval_request':
      return `aguardando aprovação: ${p.desc || ''}`;
    case 'approval_decision':
      return `decisão: ${p.approved ? 'aprovado' : 'rejeitado'}`;
    case 'complete':
      return `concluído${p.result ? `: ${p.result}` : ''}`;
    case 'error':
      return `erro: ${p.error || ''}`;
    default:
      return ev.type;
  }
}

export default function RemoteDispatchView() {
  const [activeId, setActiveId] = useState(null);
  const [recent, setRecent] = useState([]);
  const [caps, setCaps] = useState({});
  const [presence, setPresence] = useState({ online: false });
  const { events, status, pendingApproval, error } = useDispatchStream(activeId);

  const reloadRecent = useCallback(async () => {
    try {
      setRecent(await listDispatches());
    } catch {
      setRecent([]);
    }
  }, []);

  useEffect(() => {
    if (activeId) return undefined;
    reloadRecent();
    getCapabilities().then(setCaps).catch(() => setCaps({}));
    const pollPresence = () => getPresence().then(setPresence).catch(() => setPresence({ online: false }));
    pollPresence();
    const t = setInterval(pollPresence, 5_000);
    return () => clearInterval(t);
  }, [activeId, reloadRecent]);

  const decide = useCallback(
    async (approved) => {
      if (!activeId || !pendingApproval) return;
      try {
        await sendApproval(activeId, pendingApproval.request_id, approved);
      } catch {
        /* the runner will re-poll; surfaced via stream */
      }
    },
    [activeId, pendingApproval],
  );

  const cancel = useCallback(async () => {
    if (!activeId) return;
    try {
      await requestCancel(activeId);
    } catch {
      /* ignore — best effort */
    }
  }, [activeId]);

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-base font-semibold text-apolo-textPrimary">Dispatch remoto</h1>
          {!activeId && <MacOnlineBadge online={presence.online} />}
        </div>
        {activeId && (
          <div className="flex gap-2">
            <TouchButton variant="ghost" onClick={() => setActiveId(null)}>
              Voltar
            </TouchButton>
            {status !== 'completed' && status !== 'failed' && status !== 'cancelled' && (
              <TouchButton variant="danger" onClick={cancel}>
                Cancelar
              </TouchButton>
            )}
          </div>
        )}
      </div>

      {!activeId && (
        <>
          <BrainStatus capabilities={caps} />
          <DispatchComposer onCreated={(res) => res?.id && setActiveId(res.id)} />
          <DispatchHistory dispatches={recent} onOpen={setActiveId} />
        </>
      )}

      {activeId && (
        <>
          <p className="text-xs text-apolo-textTertiary">Status: {status}</p>
          {error && <p className="text-xs text-apolo-error">{error}</p>}
          <ApprovalPrompt request={pendingApproval} onDecision={decide} />
          <ol className="flex flex-col gap-2">
            {events.map((ev, i) => {
              const key = ev.id || `${ev.seq}-${i}`;
              if (ev.type === 'diff') {
                return (
                  <li key={key}>
                    <DiffView file={ev.payload?.file} content={ev.payload?.content} patch={ev.payload?.patch} />
                  </li>
                );
              }
              if (ev.type === 'file_tree') {
                return (
                  <li key={key}>
                    <FileTreeView nodes={ev.payload?.nodes} />
                  </li>
                );
              }
              return (
                <li
                  key={key}
                  className="whitespace-pre-wrap break-words rounded-md bg-apolo-surface2 p-2 text-sm text-apolo-textSecondary"
                >
                  <span className="text-apolo-textTertiary">{ev.type}</span> {summarize(ev)}
                </li>
              );
            })}
            {events.length === 0 && (
              <li className="text-sm text-apolo-textTertiary">Aguardando o Apolo na sua máquina pegar a tarefa…</li>
            )}
          </ol>
        </>
      )}
    </div>
  );
}
